import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, Eye, Clock } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';


const GoogleAnalyticsMetrics = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [metrics, setMetrics] = useState({
    totalUsers: 2847,
    pageViews: 8934,
    avgSessionDuration: '3:24',
    bounceRate: 42.3,
    organicTraffic: 68.5,
    topPages: [
      { page: '/', views: 3421, avgTime: '2:15', bounceRate: 38.2 },
      { page: '/shipping-information', views: 2156, avgTime: '4:32', bounceRate: 28.5 },
      { page: '/tracking/:reference', views: 1876, avgTime: '1:45', bounceRate: 52.1 },
      { page: '/client-dashboard', views: 987, avgTime: '5:12', bounceRate: 22.4 },
      { page: '/admin-login', views: 494, avgTime: '0:58', bounceRate: 65.3 }
    ],
    trafficSources: [
      { source: 'Recherche organique', percentage: 68.5, users: 1950 },
      { source: 'Direct', percentage: 18.2, users: 518 },
      { source: 'Réseaux sociaux', percentage: 8.7, users: 248 },
      { source: 'Référents', percentage: 4.6, users: 131 }
    ],
    weeklyData: [
      { day: 'Lun', users: 385, pageViews: 1245 },
      { day: 'Mar', users: 412, pageViews: 1356 },
      { day: 'Mer', users: 398, pageViews: 1289 },
      { day: 'Jeu', users: 445, pageViews: 1432 },
      { day: 'Ven', users: 423, pageViews: 1378 },
      { day: 'Sam', users: 389, pageViews: 1198 },
      { day: 'Dim', users: 395, pageViews: 1036 }
    ]
  });

  const kpiCards = [
    { label: 'Utilisateurs totaux', value: metrics?.totalUsers?.toLocaleString(), icon: Users, color: 'blue', change: '+12.5%' },
    { label: 'Pages vues', value: metrics?.pageViews?.toLocaleString(), icon: Eye, color: 'green', change: '+8.3%' },
    { label: 'Durée moyenne', value: metrics?.avgSessionDuration, icon: Clock, color: 'purple', change: '+5.2%' },
    { label: 'Trafic organique', value: `${metrics?.organicTraffic}%`, icon: TrendingUp, color: 'orange', change: '+3.7%' }
  ];

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <TrendingUp className="text-green-600" size={24} />
            Métriques Google Analytics
          </h2>
          <div className="flex gap-2">
            {['7d', '30d', '90d']?.map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  timeRange === range
                    ? 'bg-green-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {range === '7d' ? '7 jours' : range === '30d' ? '30 jours' : '90 jours'}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiCards?.map((kpi, index) => {
          const Icon = kpi?.icon;
          const colorClasses = {
            blue: 'bg-blue-50 text-blue-600 border-blue-200',
            green: 'bg-green-50 text-green-600 border-green-200',
            purple: 'bg-purple-50 text-purple-600 border-purple-200',
            orange: 'bg-orange-50 text-orange-600 border-orange-200'
          };

          return (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg border ${colorClasses?.[kpi?.color]}`}>
                  <Icon size={24} />
                </div>
                <span className="text-sm font-medium text-green-600">{kpi?.change}</span>
              </div>
              <p className="text-sm text-gray-600 mb-1">{kpi?.label}</p>
              <p className="text-3xl font-bold text-gray-900">{kpi?.value}</p>
            </div>
          );
        })}
      </div>
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly Traffic Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Trafic hebdomadaire</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={metrics?.weeklyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="users" stroke="#16a34a" strokeWidth={2} name="Utilisateurs" />
              <Line type="monotone" dataKey="pageViews" stroke="#3b82f6" strokeWidth={2} name="Pages vues" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Traffic Sources */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sources de trafic</h3>
          <div className="space-y-4">
            {metrics?.trafficSources?.map((source, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900">{source?.source}</span>
                  <span className="text-sm text-gray-600">{source?.percentage}% ({source?.users} utilisateurs)</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${source?.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Top Pages Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Pages les plus visitées</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Page</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Vues</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Temps moyen</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Taux de rebond</th>
              </tr>
            </thead>
            <tbody>
              {metrics?.topPages?.map((page, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4">
                    <p className="font-medium text-gray-900">{page?.page}</p>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <p className="text-gray-900 font-medium">{page?.views?.toLocaleString()}</p>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <p className="text-gray-900">{page?.avgTime}</p>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                      page?.bounceRate < 40 ? 'bg-green-100 text-green-800' :
                      page?.bounceRate < 60 ? 'bg-orange-100 text-orange-800': 'bg-red-100 text-red-800'
                    }`}>
                      {page?.bounceRate}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Integration Status */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong>Note:</strong> Les données affichées sont des exemples. Pour voir vos données réelles, assurez-vous que votre ID Google Analytics est correctement configuré dans les variables d'environnement (VITE_GA_MEASUREMENT_ID).
        </p>
      </div>
    </div>
  );
};

export default GoogleAnalyticsMetrics;